import React, { Component } from 'react';
import clsx from 'clsx';
import { WithStyles } from '@material-ui/core/styles';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';
import Chip from '#web-components/components/Chip';
import IconButton from '#web-components/components/IconButton';
import MenuItem from '#web-components/components/MenuList/components/MenuItem';
import Input from '#web-components/components/FormControls/Input';
import { FormControlError } from '#web-components/types/formControls';
import styles from './Autocomplete.styles';

export interface ItemProps {
  value: string,
  label: string,
  originalValue?: unknown,
}

export interface AutocompleteProps {
  name: string,
  id?: string,
  value: ItemProps | ItemProps[];
  label?: string;
  onChange: (value: ItemProps | ItemProps[] | null) => void;
  options: ItemProps[];
  error?: FormControlError;
  placeholder?: string;
  multiple?: boolean;
  fullWidth?: boolean;
  noOptionsText?: string;
  disabled?: boolean;
  onInputChange?: (value: string | number) => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  hiddenLabel?: boolean;
  tabIndex?: number;
  description?: string;
  size?: 'small' | 'medium';
  disablePortal?: boolean;
  onOpen?: (event: React.ChangeEvent<unknown>) => void
  onClose?: (event: React.ChangeEvent<unknown>) => void
}

export interface Props extends WithStyles<typeof styles>, AutocompleteProps {}

export default class Autocomplete extends Component<Props> {
  handleChange = (
    event: React.ChangeEvent<Record<string, unknown>>,
    value: ItemProps[] | ItemProps | null,
  ) => {
    this.props.onChange(value);
  };

  handleInputChange = (value: string | number) => {
    const { onInputChange } = this.props;
    if (onInputChange) {
      onInputChange(value);
    }
  };

  render() {
    const {
      id,
      name,
      value,
      label,
      disabled,
      placeholder,
      options,
      multiple,
      fullWidth,
      noOptionsText,
      classes,
      hiddenLabel,
      onInputChange,
      onInputFocus,
      onInputBlur,
      tabIndex,
      error,
      description,
      size,
      disablePortal,
      onOpen,
      onClose,
    } = this.props;

    return (
      <MuiAutocomplete
        disablePortal={disablePortal}
        id={id}
        classes={{
          listbox: classes.listbox,
          popupIndicator: classes.popupIndicator,
          popupIndicatorOpen: classes.popupIndicatorOpen,
          endAdornment: classes.endAdornment,
          clearIndicator: classes.clearIndicator,
          popper: classes.popper,
          paper: classes.paper,
          inputRoot: clsx(classes.inputRoot, { [classes.small]: size === 'small' }),
          input: classes.input,
        }}
        onOpen={onOpen}
        onClose={onClose}
        fullWidth={fullWidth}
        multiple={multiple}
        disabled={disabled}
        options={options}
        getOptionLabel={(option) => option.label || ''}
        value={value}
        renderTags={(values: ItemProps[], getTagProps) => values.map((option: ItemProps | null, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={option?.value}
            label={option?.label}
          />
        ))}
        popupIcon={<IconButton size={size || 'medium'}><KeyboardArrowDownIcon /></IconButton>}
        closeIcon={<IconButton size={size || 'medium'}><CloseIcon /></IconButton>}
        onInputChange={(_, newInputValue) => {
          this.handleInputChange(newInputValue);
        }}
        renderInput={(params) => (
          <Input
            {...params}
            id={id}
            name={`${name}-input`}
            label={label}
            placeholder={placeholder}
            InputProps={params.InputProps}
              // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{ ...params.inputProps, tabIndex }}
            hideLabel={hiddenLabel}
            isLabelShrink={!!placeholder}
            disabled={disabled}
            error={error}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            description={description}
            size={size}
          />
        )}
        renderOption={(option, state) => (
          <MenuItem
            size={size}
            component="div"
            selected={state.selected}
            className={classes.menuItem}
            textClassName={classes.menuItemText}
          >
            {option.label}
          </MenuItem>
        )}
        noOptionsText={noOptionsText}
        onChange={this.handleChange}
        filterOptions={onInputChange ? () => options : undefined}
        getOptionSelected={(option, currentValue) => isEqual(
          omit(option, ['originalValue']),
          omit(currentValue, ['originalValue']),
        )}
      />
    );
  }
}
